// Shared copilot sidebar shell for analytics chat (flow and case).
// Domain-specific config (chat hook, prompts, labels) is passed as props.

import { useCallback, useEffect, useRef, useState } from "react";

import { faChartSimple } from "@fortawesome/pro-regular-svg-icons";
import { times } from "lodash-es";
import { m } from "motion/react";

import { CopilotSidebarHeader } from "src/analytics/copilot/CopilotSidebarHeader";
import {
  buildExplorePin,
  type ChartExploreContext,
  mergeExplorePins,
  pinDisplayTitle,
} from "src/analytics/copilot/chartExploreContext";
import { CHART_MENTION_KINDS } from "src/analytics/copilot/chartMentionKinds";
import {
  type DashboardPinning,
  LiveAnalyticsMessages,
} from "src/analytics/copilot/LiveAnalyticsMessages";
import { useAnalyticsChatScroll } from "src/analytics/copilot/useAnalyticsChatScroll";
import type { AnalyticsChatResult } from "src/analytics/copilot/useAnalyticsChatSession";
import {
  useDashboardMetricsParams,
  usePinRowsResolver,
} from "src/analytics/dashboard/DashboardMetricsContext";
import {
  PinChartModal,
  type PinModalData,
} from "src/analytics/dashboard/PinChartModal";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatMessageSkeleton } from "src/base-components/Chat/ChatMessageSkeleton";
import { ChatReferencesProvider } from "src/base-components/Chat/ChatReferences";
import {
  type MentionItems,
  type PromptBoxMention,
  referencesToPlainText,
} from "src/base-components/Chat/PromptBox/mentionContent";
import {
  type CustomMentionPill,
  PromptBox,
} from "src/base-components/Chat/PromptBox/PromptBox";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { ResizeHandleButton } from "src/base-components/ResizableSidepane";
import { useResizableWidth } from "src/base-components/useResizableWidth";
import { SandboxedChart } from "src/chartSandbox/SandboxedChart";
import type { QueryContext } from "src/clients/analytics-query";
import { useModal } from "src/design-system/Modal";

const MIN_WIDTH = 420;
const MAX_WIDTH = 800;

const CHART_CONTEXT_PILL_PREFIX = "chart-context:";

type AnalyticsCopilotSidebarProps = {
  onClose: () => void;
  chat: AnalyticsChatResult;
  labelDictionaries: LabelDictionaries;
  dataSource?: QueryContext;
  pinning?: DashboardPinning;
  storageKey: string;
  dataLoc: string;
  emptyStateDataLoc: string;
  suggestionPrompts: ChatEmptyStatePrompt[];
  contextPill?: CustomMentionPill;
  submitLabel?: string;
  pendingStructuredInput?: ChartExploreContext | null;
  onClearPendingStructuredInput?: () => void;
  pendingInitialMessage?: string | null;
  onClearPendingInitialMessage?: () => void;
};

export const AnalyticsCopilotSidebar: React.FC<
  AnalyticsCopilotSidebarProps
> = ({
  onClose,
  chat,
  labelDictionaries,
  dataSource,
  pinning,
  storageKey,
  dataLoc,
  emptyStateDataLoc,
  suggestionPrompts,
  contextPill,
  submitLabel,
  pendingStructuredInput,
  onClearPendingStructuredInput,
  pendingInitialMessage,
  onClearPendingInitialMessage,
}) => {
  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "right",
    storageKey,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: 528,
  });

  const listRef = useRef<HTMLUListElement>(null);
  const lastTurnRef = useRef<HTMLLIElement>(null);

  const {
    isActive,
    isProvisioning,
    isReady,
    isExpired,
    failureMessage,
    isRestoringSession,
    messages,
    send,
    resetSession,
  } = chat;

  const { environment, timeWindow } = useDashboardMetricsParams();
  const resolvePinRows = usePinRowsResolver();

  const { reservesTurnHeight, snapPromptToTop } = useAnalyticsChatScroll({
    listRef,
    lastTurnRef,
    messages,
    isActive,
    failureMessage,
  });

  const busy = isActive || !isReady || isExpired;

  const handleSubmit = useCallback(
    async (content: string, mentions: PromptBoxMention[] = []) => {
      const message = referencesToPlainText(content);
      if (!message.trim() || busy) return;
      const mentionedPinIds = [
        ...new Set(
          mentions
            .map((mention) =>
              typeof mention.data?.id === "string" ? mention.data.id : null,
            )
            .filter((id): id is string => id !== null),
        ),
      ];
      const mentionedPins = pinning
        ? mentionedPinIds.flatMap((pinId) => {
            const pin = pinning.dashboard.pins[pinId];
            return pin
              ? [
                  buildExplorePin(
                    pin,
                    resolvePinRows(pinId),
                    environment,
                    timeWindow,
                  ),
                ]
              : [];
          })
        : [];
      const structuredInput = mergeExplorePins(
        pendingStructuredInput,
        mentionedPins.length > 0 ? { pins: mentionedPins } : null,
      );
      // The context pill clears at dispatch, like the message text and
      // mention pills; a failed invoke surfaces via failureMessage.
      if (pendingStructuredInput) onClearPendingStructuredInput?.();
      snapPromptToTop();
      try {
        await send(message, structuredInput ?? undefined);
      } catch {
        // Failures surface via failureMessage.
      }
    },
    [
      busy,
      send,
      snapPromptToTop,
      pendingStructuredInput,
      onClearPendingStructuredInput,
      pinning,
      resolvePinRows,
      environment,
      timeWindow,
    ],
  );

  const [promptDraft, setPromptDraft] = useState("");
  const sentInitialMessageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingInitialMessage) {
      sentInitialMessageRef.current = null;
      return;
    }
    if (sentInitialMessageRef.current === pendingInitialMessage) return;
    if (!isExpired && (!isReady || isActive || isRestoringSession)) return;
    sentInitialMessageRef.current = pendingInitialMessage;
    onClearPendingInitialMessage?.();
    if (isExpired) setPromptDraft(pendingInitialMessage);
    else void handleSubmit(pendingInitialMessage);
  }, [
    pendingInitialMessage,
    isReady,
    isActive,
    isExpired,
    isRestoringSession,
    handleSubmit,
    onClearPendingInitialMessage,
  ]);

  const {
    isOpen: isPinModalOpen,
    openModal: openPinModal,
    closeModal: closePinModal,
    afterLeave: afterPinModalLeave,
    data: pinData,
  } = useModal<PinModalData>();

  const dashboardPins = pinning ? Object.values(pinning.dashboard.pins) : [];
  const mentionItems: MentionItems | undefined =
    dashboardPins.length > 0
      ? dashboardPins.map((pin) => ({
          value: pinDisplayTitle(pin),
          id: pin.id,
          kind: "chart",
        }))
      : undefined;

  const chartContextPills: CustomMentionPill[] = (
    pendingStructuredInput?.pins ?? []
  ).map((pin) => ({
    key: `${CHART_CONTEXT_PILL_PREFIX}${pin.pin_id}`,
    label: pin.name,
    icon: faChartSimple,
    isDeletable: onClearPendingStructuredInput !== undefined,
  }));
  const promptPills = [
    ...(contextPill ? [contextPill] : []),
    ...chartContextPills,
  ];

  const handleDeletePill = useCallback(
    (key: string) => {
      if (key.startsWith(CHART_CONTEXT_PILL_PREFIX)) {
        onClearPendingStructuredInput?.();
      }
    },
    [onClearPendingStructuredInput],
  );

  const handlePinConfirm = (title: string) => {
    if (pinData && pinning) {
      pinning.pinChart(
        {
          type: "chart",
          query: { query: pinData.sql, parameters: pinData.parameterNames },
          chart_definition: pinData.chartDefinition,
        },
        pinData.pinId,
        title,
      );
    }
    closePinModal();
  };

  return (
    <FloatingWindowsProvider>
      <m.div
        className="shrink-0 overflow-hidden"
        style={{ width }}
        initial={false}
        animate={{ width }}
        exit={{ width: 0 }}
        transition={{
          duration: isResizing ? 0 : 0.2,
          ease: "easeOut",
        }}
      >
        <aside
          className="relative flex h-full max-h-screen flex-col border-gray-200 border-l bg-white"
          data-loc={dataLoc}
          style={{ width }}
        >
          <ResizeHandleButton onResizeStart={handleResizeStart} />

          <div className="shrink-0 px-4">
            <CopilotSidebarHeader
              isNewConversation={messages.length === 0 && !failureMessage}
              onClose={onClose}
              onNewConversation={resetSession}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            <ChatReferencesProvider
              items={mentionItems}
              kinds={CHART_MENTION_KINDS}
            >
              <ChatBody>
                <ChatMessageList ref={listRef} smoothScroll>
                  {isRestoringSession ? (
                    times(3, (i) => (
                      <ChatMessageSkeleton
                        key={i}
                        dataLoc="analytics-restore-skeleton"
                      />
                    ))
                  ) : messages.length === 0 && !isActive && !failureMessage ? (
                    <ChatEmptyState
                      dataLoc={emptyStateDataLoc}
                      prompts={suggestionPrompts}
                      variant="new"
                      onPromptClick={handleSubmit}
                    />
                  ) : (
                    <LiveAnalyticsMessages
                      dataSource={dataSource}
                      failureMessage={failureMessage}
                      isActive={isActive}
                      isProvisioning={isProvisioning}
                      labelDictionaries={labelDictionaries}
                      lastTurnRef={lastTurnRef}
                      messages={messages}
                      onRequestPin={pinning ? openPinModal : undefined}
                      pinning={pinning}
                      reservesTurnHeight={reservesTurnHeight}
                    />
                  )}
                </ChatMessageList>

                <ChatInputArea>
                  <PromptBox
                    key={promptDraft}
                    customMentionPills={promptPills}
                    dataLoc="copilot-prompt"
                    disabled={false}
                    initialValue={promptDraft}
                    placeholder="Message Copilot..."
                    referenceButtonAriaLabel="Mention a chart"
                    referenceHint="Use @ to refer to other charts"
                    submitDisabled={busy}
                    submitLabel={submitLabel}
                    onDeleteMention={handleDeletePill}
                    onSubmit={handleSubmit}
                  />
                </ChatInputArea>
              </ChatBody>
            </ChatReferencesProvider>
          </div>
        </aside>
      </m.div>
      <PinChartModal
        afterLeave={afterPinModalLeave}
        defaultTitle={pinData?.defaultTitle ?? ""}
        open={isPinModalOpen}
        onClose={closePinModal}
        onConfirm={handlePinConfirm}
      >
        {pinData && (
          <SandboxedChart
            chartDefinition={pinData.chartDefinition}
            labelDictionaries={pinData.labelDictionaries}
            rows={pinData.rows}
          />
        )}
      </PinChartModal>
    </FloatingWindowsProvider>
  );
};
